import { Icon } from '@iconify/react'
import { Button, Divider, Tooltip } from '@nextui-org/react'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import Mixpanel from 'src/lib/mixpanel'
import TableOfContentDetails from './TableOfContentDetails'
import TableOfContentDesign from './TableOfContentDesign'
import TOCModel1 from './Designs/Model1'

export default function TableOfContentPage({ isVisible, doc, close, setDoc, loadDocument, variant }: ITableOfContentPageProps) {
  const [isLeftSidebarOpen, setLeftSidebarOpen] = useState(true)
  const [currentSidebarContent, setCurrentSidebarContent] = useState('details')
  const [tableOfContentDesign, setTableOfContentDesign] = useState<number>(1)
  const handleCloseSidebar = () => {
    setLeftSidebarOpen(false)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close?.()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [close])

  const handleSidebarToggle = (content: string) => {
    setLeftSidebarOpen(true)
    setCurrentSidebarContent(content)
  }
  const tableOfContentDesignProps = {
    tableOfContentDesign,
    setTableOfContentDesign,
  }
  return (
    isVisible && (
      <div className="absolute inset-0 flex justify-center z-[60]">
        <div className="absolute inset-0 overflow-hidden rounded-b-lg bg-white flex flex-1">
          {/* Left sidebar */}
          <div className="fixed top-[60px] bottom-[60px] left-[30px] w-[100px] max-w-[100px] h-[calc(100vh-120px)] bg-white border-1 border-gray-200 flex flex-col items-center py-4 px-2 rounded-2xl z-40 gap-6">
            {/* Middle Section */}
            <div className="flex flex-col items-center gap-4 w-full">
              <p className="text-xs text-gray-500 tracking-widest text-center">DETAILS</p>
              <Tooltip content="Details" color="foreground" showArrow={true} placement="right">
                <Button
                  onPress={() => {
                    Mixpanel.track('Table of Content Page Details button clicked', {
                      button_name: 'Details',
                      page_name: window.location.pathname,
                      location: 'Table of Content Page Sidebar',
                    })
                    handleSidebarToggle('details')
                  }}
                  variant="light"
                  isIconOnly
                >
                  <Icon icon="lucide:clipboard-type" className="text-secondary" width="30" />
                </Button>
              </Tooltip>
            </div>

            {/* Bottom Section */}
            <div className="flex flex-col items-center gap-4 w-full hidden">
              <Divider className="h-[3px]" />
              <p className="text-xs text-gray-500 tracking-widest text-center">DESIGN</p>
              <Tooltip content="Design" color="foreground" showArrow={true} placement="right">
                <Button
                  onPress={() => {
                    Mixpanel.track('Cover Page Design button clicked', {
                      button_name: 'Design',
                      page_name: window.location.pathname,
                      location: 'Cover Page Sidebar',
                    })
                    handleSidebarToggle('design')
                  }}
                  variant="light"
                  isIconOnly
                >
                  <Icon icon="lucide:layout-template" className="text-secondary" width="30" />
                </Button>
              </Tooltip>
            </div>
          </div>

          {/* Left sidebar content */}
          {isLeftSidebarOpen && (
            <motion.div
              layout
              key={currentSidebarContent}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-[60px] bottom-[60px] left-[140px] w-[300px] h-[calc(100vh-120px)] bg-white border rounded-xl p-5 z-50 overflow-y-scroll"
            >
              {currentSidebarContent === 'details' && (
                <TableOfContentDetails setDoc={setDoc} doc={doc} loadDocument={loadDocument} variant={variant} />
              )}
              {currentSidebarContent === 'design' && (
                <TableOfContentDesign
                  tableOfContentDesignProps={tableOfContentDesignProps}
                  onClose={handleCloseSidebar}
                />
              )}
            </motion.div>
          )}

          {/* Table of content page */}
          <button className={'absolute top-10 right-10 text-black'} onClick={() => close()}>
            <Icon icon="lucide:x" width="24" />
          </button>

          <div className={'flex flex-1 justify-center items-center bg-whites pl-[440px] py-5'}>
            <div
              className="bg-white rounded-xl p-8 border w-[70%] max-w-[calc(100vh*0.7)] h-full"
              style={{
                padding: '0.5in',
                borderColor: 'none',
              }}
              id={'cover-page-component'}
            >
              <TOCModel1 doc={doc} />
            </div>
          </div>
        </div>
      </div>
    )
  )
}

interface ITableOfContentPageProps {
  isVisible: boolean
  close: () => void
  doc: any
  setDoc: (value: any) => void
  loadDocument: () => void
  variant: string
}
